import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';

const FlightContext = createContext();

export const useFlight = () => {
  const context = useContext(FlightContext);
  if (!context) {
    throw new Error('useFlight must be used within a FlightProvider');
  }
  return context;
};

export const FlightProvider = ({ children }) => {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAircraftModel, setFilterAircraftModel] = useState('all');
  const [filterDataset, setFilterDataset] = useState('5days');
  const [aircraftModels, setAircraftModels] = useState([]);

  const calculateStats = useCallback((flightData) => {
    const uniqueRoutes = new Set(flightData.map(f => `${f.origin}-${f.destination}`)).size;
    const uniqueAircraft = new Set(flightData.map(f => f.registration)).size;
    const destinations = new Set(flightData.map(f => f.destination)).size;
    
    setStats({
      totalFlights: flightData.length,
      uniqueRoutes,
      uniqueAircraft,
      destinations
    });
  }, []);

  const fetchFleetInformation = useCallback(async () => {
    try {
      const sheetId = process.env.REACT_APP_FLEET_INFO_SHEET_ID;
      const url = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
      
      const response = await fetch(url);
      const csvText = await response.text();
      
      Papa.parse(csvText, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          const models = results.data
            .map(row => row['Model'] || row['model'])
            .filter(model => model && model.trim() !== '')
            .map(model => model.trim());
          
          const uniqueModels = [...new Set(models)].sort();
          setAircraftModels(uniqueModels);
        },
        error: (err) => {
          console.error('Fleet CSV parsing error:', err);
        }
      });
    } catch (err) {
      console.error('Failed to fetch fleet data:', err);
    }
  }, []);

  const fetchFlightData = useCallback(async () => {
    try {
      setLoading(true);
      const sheetId = process.env.REACT_APP_FLIGHT_DATA_SHEET_ID;
      const url = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
      
      const response = await fetch(url);
      const csvText = await response.text();
      
      Papa.parse(csvText, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          const processedFlights = results.data
            .filter(f => {
              const hasOrigin = f['Origin IATA'] || f['Origin'];
              const hasDest = f['Destination IATA'] || f['Destination'];
              return hasOrigin && hasDest;
            })
            .map((flight, idx) => {
              return {
                id: flight['Flight ID'] || `flight-${idx}`,
                flightNumber: flight['Flight Number'] || flight['FlightNumber'],
                callSign: flight['Call Sign'] || flight['CallSign'],
                origin: flight['Origin IATA'] || flight['Origin'],
                originName: flight['Origin Name'] || flight['OriginName'],
                destination: flight['Destination IATA'] || flight['Destination'],
                destinationName: flight['Destination Name'] || flight['DestinationName'],
                aircraft: flight['Aircraft Model'] || flight['AircraftModel'],
                registration: flight['Aircraft Registration'] || flight['AircraftRegistration'],
                scheduledDep: flight['Scheduled Departure (UTC)'] || flight['ScheduledDeparture'],
                actualDep: flight['Actual Departure (UTC)'] || flight['ActualDeparture'],
                scheduledArr: flight['Scheduled Arrival (UTC)'] || flight['ScheduledArrival'],
                actualArr: flight['Actual Arrival (UTC)'] || flight['ActualArrival'],
                status: flight['Status'],
                terminal: flight['Terminal'],
                gate: flight['Gate'],
                dateCollected: flight['Date Collected (UTC)'] || flight['DateCollected']
              };
            });
          
          setFlights(processedFlights);
          calculateStats(processedFlights);
          setLoading(false);
        },
        error: (err) => {
          console.error('CSV parsing error:', err);
          setError('Failed to parse CSV data: ' + err.message);
          setLoading(false);
        }
      });
    } catch (err) {
      setError('Failed to fetch data: ' + err.message);
      setLoading(false);
    }
  }, [calculateStats]);

  useEffect(() => {
    fetchFlightData();
    fetchFleetInformation();
  }, [fetchFlightData, fetchFleetInformation]);

    const filteredFlights = flights.filter(f => {
    const statusMatch = filterStatus === 'all' || f.status?.toLowerCase() === filterStatus.toLowerCase();
    const modelMatch = filterAircraftModel === 'all' || f.aircraft === filterAircraftModel;
    
    // Dataset filter based on dateCollected
    let datasetMatch = true;
    if (filterDataset === '5days' && f.dateCollected) {
      try {
        // Safari-compatible date parsing
        // Handle formats like "2025-10-25Z" or "2025-10-25"
        let dateString = f.dateCollected.trim();
        
        // Ensure proper ISO format for Safari
        if (dateString.endsWith('Z') && !dateString.includes('T')) {
          // Convert "2025-10-25Z" to "2025-10-25T00:00:00Z"
          dateString = dateString.replace('Z', 'T00:00:00Z');
        } else if (!dateString.includes('T')) {
          // Convert "2025-10-25" to "2025-10-25T00:00:00Z"
          dateString = dateString + 'T00:00:00Z';
        }
        
        const collectedDate = new Date(dateString);
        
        // Check if date is valid
        if (isNaN(collectedDate.getTime())) {
          console.warn('Invalid date format:', f.dateCollected);
          datasetMatch = true; // Include flight if date is invalid
        } else {
          // Get today's date at UTC midnight
          const now = new Date();
          const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
          
          // Get the date 5 days ago at UTC midnight
          const fiveDaysAgo = new Date(todayUTC);
          fiveDaysAgo.setUTCDate(todayUTC.getUTCDate() - 5);
          
          // Check if the collected date is within the past 5 days
          datasetMatch = collectedDate >= fiveDaysAgo && collectedDate < todayUTC;
        }
      } catch (error) {
        console.error('Date parsing error:', error, 'for date:', f.dateCollected);
        datasetMatch = true; // Include flight if there's an error
      }
    }
  
    return statusMatch && modelMatch && datasetMatch;
  });

  const value = {
    flights,
    filteredFlights,
    loading,
    error,
    stats,
    filterStatus,
    setFilterStatus,
    filterAircraftModel,
    setFilterAircraftModel,
    filterDataset,
    setFilterDataset,
    aircraftModels,
    refetch: fetchFlightData
  };

  return (
    <FlightContext.Provider value={value}>
      {children}
    </FlightContext.Provider>
  );
};